import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, User, Calendar, IndianRupee, Sparkles, Target, Clock, Eye, AlertCircle, List, X, Filter, Search } from 'lucide-react';
import apiService from '../services/api';
import { jwtDecode } from "jwt-decode";
import AlertBox from '../../Components/AlertBox';
const ProjectTracker = ({ projects, setProjects, milestones, setMilestones, onNavigate }) => {
  const navigate = useNavigate();

  // Local state for real-time updates
  const [localProjects, setLocalProjects] = useState(projects);
  const [localMilestones, setLocalMilestones] = useState(milestones);
  const [employees, setEmployees] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [statusOptions, setStatusOptions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // States
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredColumn, setHoveredColumn] = useState(null);
  const [hoveredEmpty, setHoveredEmpty] = useState(null);
  const [showAlert, setShowAlert] = useState(null);
  // Initial data fetch and setup
  useEffect(() => {
    fetchProjects();
    fetchMilestones();
    fetchEmployees();
    fetchCustomStatuses();
  }, []);

  // Set up global refresh function for cross-component updates
  useEffect(() => {
    console.log('ProjectTracker - Setting up window.refreshProjectTracker');
    window.refreshProjectTracker = () => {
      console.log('ProjectTracker - Global refresh triggered');
      fetchProjects();
      fetchMilestones();
    };

    return () => {
      console.log('ProjectTracker - Cleaning up window.refreshProjectTracker');
      delete window.refreshProjectTracker;
    };
  }, []);

  // Listen for project updates from other components
  useEffect(() => {
    const handleProjectUpdate = () => {
      console.log('ProjectTracker - Received project update event');
      fetchProjects();
    };

    // Listen for custom events
    window.addEventListener('projectsUpdated', handleProjectUpdate);

    return () => {
      window.removeEventListener('projectsUpdated', handleProjectUpdate);
    };
  }, []);

  // Update local state when props change
  useEffect(() => {
    setLocalProjects(projects);
  }, [projects]);

  useEffect(() => {
    setLocalMilestones(milestones);
  }, [milestones]);

  // Fetch functions (keeping existing logic)
  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('authToken');
      console.log('ProjectTracker - Fetching projects with token:', !!token);

      if (!token) {
        console.error('ProjectTracker - No access token found');
        setLocalProjects([]);
        return;
      }

      const decodedToken = jwtDecode(token);
      const userRole = decodedToken.role;
      const userId = decodedToken.id || decodedToken._id;
      console.log('ProjectTracker - User role:', userRole, 'User ID:', userId);

      // Use the same endpoint as ProjectList.jsx
      const response = await fetch('https://slateblue-mallard-430836.hostingersite.com/api/projectsmgnt', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('ProjectTracker - Projects API response status:', response.status);

      if (response.ok) {
        let data = await response.json();
        console.log('ProjectTracker - Raw projects response:', data);
        // ProjectList.jsx directly uses the data array
        let projectList = Array.isArray(data) ? data : [];
        console.log('ProjectTracker - Processed project list (before filter):', projectList.length);

        // Filter projects based on user role
        if (userRole === 'employee' || userRole === 'team_member') {
          projectList = projectList.filter(project => {
            const isTeamMember = project.teamMembers && Array.isArray(project.teamMembers) &&
              project.teamMembers.some(member => {
                const memberId = typeof member === 'object' ? (member._id || member.id) : member;
                return String(memberId) === String(userId);
              });

            console.log(`ProjectTracker - Project "${project.title || project.name}" - isTeamMember:`, isTeamMember);
            return isTeamMember;
          });

          console.log('ProjectTracker - Filtered projects for team member:', projectList.length);
        }

        console.log('ProjectTracker - Sample project:', projectList[0]);
        console.log('ProjectTracker - Total projects found:', projectList.length);
        setLocalProjects(projectList);
      } else {
        console.error('ProjectTracker - Failed to fetch projects:', response.status);
        setLocalProjects([]);
      }
    } catch (error) {
      console.error('ProjectTracker - Error fetching projects:', error);
      setLocalProjects([]);
    }
  };

  const fetchMilestones = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('https://slateblue-mallard-430836.hostingersite.com/api/milestones', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        let milestones = data.milestones || data;
        console.log('ProjectTracker - Fetched milestones (before filter):', Array.isArray(milestones) ? milestones.length : 0);

        // Filter milestones based on user role
        let decodedToken;
        try {
          decodedToken = jwtDecode(token);
        } catch (e) {
          console.error('ProjectTracker - Error decoding token:', e);
        }

        if (decodedToken) {
          const userRole = decodedToken.role;
          const userId = decodedToken.id || decodedToken._id;
          console.log('ProjectTracker - User role:', userRole, 'User ID:', userId);

          // Normalize role to lowercase for comparison
          const normalizedRole = userRole ? userRole.toLowerCase() : '';

          if (Array.isArray(milestones) && (normalizedRole === 'employee' || normalizedRole === 'team_member')) {
            console.log('ProjectTracker - Applying filter for employee/team_member');
            const originalCount = milestones.length;

            milestones = milestones.filter(milestone => {
              const assignedTeamMember = milestone.assignedTeamMember;

              if (!assignedTeamMember) return false;

              // Handle array of assigned members
              if (Array.isArray(assignedTeamMember)) {
                const isAssigned = assignedTeamMember.some(member => {
                  const memberId = typeof member === 'object' ? (member._id || member.id) : member;
                  return String(memberId) === String(userId);
                });
                if (isAssigned) console.log(`ProjectTracker - Milestone "${milestone.title}" - MATCH FOUND`);
                return isAssigned;
              }

              // Handle single assigned member
              const memberId = typeof assignedTeamMember === 'object' ?
                (assignedTeamMember._id || assignedTeamMember.id) : assignedTeamMember;
              const isAssigned = String(memberId) === String(userId);
              if (isAssigned) console.log(`ProjectTracker - Milestone "${milestone.title}" - MATCH FOUND`);
              return isAssigned;
            });

            console.log(`ProjectTracker - Filtering complete. Kept ${milestones.length} out of ${originalCount} milestones.`);
          }
        }

        setLocalMilestones(Array.isArray(milestones) ? milestones : []);

        // Filter for overdue and upcoming project-tasks (next 7 days)
        const now = new Date();
        const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

        const overdueAndUpcoming = (Array.isArray(milestones) ? milestones : []).filter(milestone => {
          if (!milestone.dueDate) return false;
          const dueDate = new Date(milestone.dueDate);
          // Include overdue (past due) and upcoming (next 7 days)
          return dueDate <= sevenDaysFromNow;
        });

        console.log('ProjectTracker - Overdue and upcoming project-tasks:', overdueAndUpcoming);
        console.log('ProjectTracker - Sample milestone data:', overdueAndUpcoming[0]);
        if (overdueAndUpcoming[0]) {
          console.log('ProjectTracker - Milestone fields:', Object.keys(overdueAndUpcoming[0]));
          console.log('ProjectTracker - Project ID field:', overdueAndUpcoming[0].project || overdueAndUpcoming[0].projectId);
          console.log('ProjectTracker - Assigned member field:', overdueAndUpcoming[0].assignedTeamMember);
        }
      } else {
        console.error('ProjectTracker - Failed to fetch milestones:', response.status);
        setLocalMilestones([]);
      }
    } catch (error) {
      console.error('ProjectTracker - Error fetching milestones:', error);
      setLocalMilestones([]);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('https://slateblue-mallard-430836.hostingersite.com/api/employees', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const employeeList = data.employees || data;
        console.log('ProjectTracker - Fetched employees:', employeeList);
        setEmployees(Array.isArray(employeeList) ? employeeList : []);
      } else {
        console.error('ProjectTracker - Failed to fetch employees:', response.status);
        setEmployees([]);
      }
    } catch (error) {
      console.error('ProjectTracker - Error fetching employees:', error);
      setEmployees([]);
    }
  };

  // Function to fetch custom statuses from API
  const fetchCustomStatuses = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('https://slateblue-mallard-430836.hostingersite.com/api/prjCustomStatus/', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const statuses = await response.json();
        console.log('ProjectTracker - API Response:', statuses);
        console.log('ProjectTracker - Response type:', typeof statuses);
        console.log('ProjectTracker - Is array:', Array.isArray(statuses));

        // Handle wrapped response format {statuses: Array}
        let statusArray = [];
        if (Array.isArray(statuses)) {
          statusArray = statuses;
        } else if (statuses && statuses.statuses && Array.isArray(statuses.statuses)) {
          console.log('ProjectTracker - Found wrapped statuses array');
          statusArray = statuses.statuses;
        } else {
          console.log('ProjectTracker - Invalid response format:', statuses);
          setStatusOptions([]);
          return;
        }

        if (statusArray.length > 0) {
          console.log('ProjectTracker - First status object:', statusArray[0]);
          console.log('ProjectTracker - First status keys:', Object.keys(statusArray[0]));

          // Extract status names from the array
          const statusNames = statusArray.map(status => {
            console.log('ProjectTracker - Processing status:', status);
            // Try to get the name field
            if (status && typeof status === 'object' && status.name) {
              console.log('ProjectTracker - Found name:', status.name);
              return status.name;
            }
            console.log('ProjectTracker - No name field found');
            return null;
          }).filter(name => name); // Remove null values

          console.log('ProjectTracker - Extracted status names:', statusNames);
          setStatusOptions(statusNames);
        } else {
          console.log('ProjectTracker - Empty status array');
          setStatusOptions([]);
        }
      } else {
        console.error('ProjectTracker - API Error:', response.status);
        setStatusOptions([]);
      }
    } catch (error) {
      console.error('ProjectTracker - Fetch Error:', error);
      setStatusOptions([]);
    }
  };

  // Helper functions
  const getEmployeeName = (assignedTeamMember) => {
    console.log('ProjectTracker - Looking for assigned team member:', assignedTeamMember);
    console.log('ProjectTracker - Available employees:', employees.map(emp => ({ id: emp._id, name: emp.name || emp.teamMemberName })));

    if (!assignedTeamMember) {
      return 'Unassigned';
    }

    // Handle case where assignedTeamMember is an object
    if (typeof assignedTeamMember === 'object') {
      const result = assignedTeamMember.teamMemberName || assignedTeamMember.name || 'Unknown';
      console.log('ProjectTracker - Employee name from object:', result);
      return result;
    }

    // Handle case where assignedTeamMember is an ID string
    const employee = employees.find(emp => emp._id === assignedTeamMember);
    const result = employee ? (employee.teamMemberName || employee.name || 'Unknown') : 'Unknown Employee';
    console.log('ProjectTracker - Employee name result:', result);
    return result;
  };

  const getProjectNameForMilestone = (milestone) => {
    console.log('ProjectTracker - Full milestone object:', milestone);

    // First priority: Use projectName directly from milestone (most reliable)
    if (milestone.projectName) {
      console.log('ProjectTracker - Using projectName from milestone:', milestone.projectName);
      return milestone.projectName;
    }

    // Second priority: Try to find project by ID
    const projectId = milestone.projectId || milestone.project;
    console.log('ProjectTracker - Looking for project ID:', projectId);
    console.log('ProjectTracker - Available projects:', localProjects.map(p => ({ id: p._id, name: p.title || p.name })));

    if (projectId && localProjects.length > 0) {
      // Try exact match first
      let project = localProjects.find(p => p._id === projectId);

      // If not found, try string comparison
      if (!project) {
        project = localProjects.find(p => p._id?.toString() === projectId?.toString());
      }

      if (project) {
        const result = project.title || project.name;
        console.log('ProjectTracker - Found project by ID:', result);
        return result;
      }
    }

    // Third priority: Check if project field is an object with name
    if (milestone.project && typeof milestone.project === 'object') {
      const result = milestone.project.title || milestone.project.name;
      if (result) {
        console.log('ProjectTracker - Using project object name:', result);
        return result;
      }
    }

    console.log('ProjectTracker - No project name found, using fallback');
    return 'Unknown Project';
  };

  const handleCardClick = (item, event) => {
    event.stopPropagation();
    console.log('ProjectTracker - Item clicked:', item);
    console.log('ProjectTracker - Item type:', item.type);

    // Only navigate for projects, not for milestones/tasks
    if (item.type === 'project') {
      console.log('ProjectTracker - Navigating to project:', item._id);
      if (onNavigate) {
        onNavigate(`/projects/${item._id}`, 'Project Details');
      } else {
        navigate(`/projects/${item._id}`);
      }
    } else if (item.type === 'milestone') {
      // For milestones/tasks, do nothing to prevent navigation
      // The modal/details view should be handled by a separate button or action
      console.log('ProjectTracker - Milestone clicked, no navigation');
      return;
    }
  };

  // Organize data into columns using dynamic statuses
  const safeProjects = Array.isArray(localProjects) ? localProjects : [];
  const safeMilestones = Array.isArray(localMilestones) ? localMilestones : [];

  // Create dynamic columns based on available statuses
  const getColumns = () => {
    const allStatuses = statusOptions.length > 0 ? statusOptions : ['Planning', 'In Progress', 'Completed'];
    const columns = {};

    allStatuses.forEach((status, index) => {
      const colors = ['#ef4444', '#8b5cf6', '#3b82f6', '#f59e0b', '#10b981', '#8b5cf6'];
      const icons = [Clock, Target, Sparkles, AlertCircle, Eye, Plus];

      columns[status.toLowerCase().replace(/\s+/g, '-')] = {
        title: status,
        items: [
          ...safeProjects.filter(p => p.status === status).map(p => ({ ...p, type: 'project', id: p._id })),
          ...safeMilestones.filter(m => m.status === status).map(m => ({ ...m, type: 'milestone', id: m._id }))
        ],
        color: colors[index % colors.length],
        icon: icons[index % icons.length]
      };
    });

    return columns;
  };

  const columns = getColumns();

  // Styles
  const projectTrackerStyle = {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    padding: '2rem',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
    position: 'relative',
    overflow: 'hidden'
  };

  const contentWrapperStyle = {
    position: 'relative',
    zIndex: 1
  };

  const pageHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
    padding: '2rem',
    background: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(25px)',
    borderRadius: '2rem',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
    position: 'relative',
    overflow: 'hidden',
    animation: 'slideInDown 0.8s ease-out'
  };

  const headerIconStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  };

  const headerTitleStyle = {
    fontSize: '2.5rem',
    fontWeight: 900,
    color: '#1f2937',
    margin: 0,
    letterSpacing: '1px'
  };

  const keyframesStyle = `
    @keyframes slideInDown {
      from {
        opacity: 0;
        transform: translateY(-30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `;

  return (
    <>
      <style>{keyframesStyle}</style>
      <div style={projectTrackerStyle}>
        <div style={contentWrapperStyle}>

          {/* List View */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.5rem',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            animation: 'fadeInUp 1s ease-out 0.3s both'
          }}>
            {/* List View Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem 1.5rem',
              backgroundColor: '#f9fafb',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.25rem',
                fontWeight: 700,
                color: '#1f2937'
              }}>
                Project List View
              </h3>

              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {/* Inline Search Bar */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  backgroundColor: 'white',
                  padding: '0.4rem 0.75rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #d1d5db',
                  minWidth: '220px'
                }}>
                  <Search size={18} style={{ color: '#6b7280' }} />
                  <input
                    type="text"
                    placeholder="Search projects..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#1f2937',
                      fontSize: '0.85rem',
                      outline: 'none',
                      width: '100%'
                    }}
                  />
                </div>

                {/* Inline Status Filter */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  backgroundColor: 'white',
                  padding: '0.4rem 0.75rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #d1d5db'
                }}>
                  <Filter size={18} style={{ color: '#6b7280' }} />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#1f2937',
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      outline: 'none'
                    }}
                  >
                    <option value="all">All Status</option>
                    {statusOptions.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Column Headers */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr',
              gap: '1rem',
              padding: '1rem 1.5rem',
              backgroundColor: '#f9fafb',
              borderBottom: '1px solid #e5e7eb',
              fontWeight: 700,
              fontSize: '0.875rem',
              color: '#374151'
            }}>
              <div>Name</div>
              <div>Type</div>
              <div>Status</div>
              <div>Due Date</div>
              <div>Budget</div>
              <div>Team</div>
            </div>

            {/* List View Content */}
            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {(() => {
                // Show projects + project-tasks due within 1 day
                const now = new Date();
                const oneDayFromNow = new Date(now.getTime() + 24 * 60 * 60 * 1000);

                // Filter milestones to show only those due within next 1 day (not overdue)
                const urgentMilestones = safeMilestones.filter(m => {
                  if (!m.dueDate) return false;
                  const dueDate = new Date(m.dueDate);
                  // Show only if due within next 24 hours (not overdue)
                  return dueDate > now && dueDate <= oneDayFromNow;
                });

                const allItems = [
                  ...safeProjects.map(p => ({ ...p, type: 'project', id: p._id })),
                  ...urgentMilestones.map(m => ({ ...m, type: 'milestone', id: m._id }))
                ];

                // Filter by search query and status
                let filteredItems = allItems;

                // Apply search filter
                if (searchQuery.trim()) {
                  const query = searchQuery.toLowerCase().trim();
                  filteredItems = filteredItems.filter(item => {
                    const name = (item.name || item.title || '').toLowerCase();
                    const projectName = (item.projectName || '').toLowerCase();
                    const description = (item.description || '').toLowerCase();
                    return name.includes(query) || projectName.includes(query) || description.includes(query);
                  });
                }

                // Apply status filter
                if (statusFilter !== 'all') {
                  filteredItems = filteredItems.filter(item => item.status === statusFilter);
                }

                // Check for empty states
                const hasNoData = safeProjects.length === 0 && safeMilestones.length === 0;
                const hasNoResults = filteredItems.length === 0 && (safeProjects.length > 0 || safeMilestones.length > 0);

                if (hasNoData) {
                  return (
                    <div style={{
                      textAlign: 'center',
                      padding: '4rem 2rem',
                      color: '#6b7280'
                    }}>
                      <Sparkles size={48} style={{ opacity: 0.5, marginBottom: '1rem' }} />
                      <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem', fontWeight: 600 }}>
                        No tasks yet
                      </h3>
                      <p style={{ margin: 0, fontSize: '0.875rem' }}>
                        Projects and project-tasks will appear here when available
                      </p>
                    </div>
                  );
                }

                if (hasNoResults) {
                  return (
                    <div style={{
                      textAlign: 'center',
                      padding: '4rem 2rem',
                      color: '#6b7280'
                    }}>
                      <Search size={48} style={{ opacity: 0.5, marginBottom: '1rem' }} />
                      <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem', fontWeight: 600 }}>
                        No results found
                      </h3>
                      <p style={{ margin: 0, fontSize: '0.875rem' }}>
                        {searchQuery ? `No projects found matching "${searchQuery}"` : 'No projects match the selected filters'}
                      </p>
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          style={{
                            marginTop: '1rem',
                            padding: '0.5rem 1rem',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            cursor: 'pointer',
                            fontSize: '0.875rem'
                          }}
                        >
                          Clear search
                        </button>
                      )}
                    </div>
                  );
                }

                return filteredItems.map((item, index) => {
                  const isProject = item.type === 'project';
                  // Get status color based on item status
                  const getStatusColor = (status) => {
                    const statusLower = status?.toLowerCase() || '';
                    if (statusLower.includes('planning') || statusLower.includes('todo')) return '#ef4444';
                    if (statusLower.includes('progress') || statusLower.includes('working')) return '#8b5cf6';
                    if (statusLower.includes('completed') || statusLower.includes('done')) return '#3b82f6';
                    return '#f59e0b'; // Default color for other statuses
                  };
                  const statusColor = getStatusColor(item.status);

                  return (
                    <div
                      key={item.id || index}
                      onClick={(event) => handleCardClick(item, event)}
                      onMouseEnter={() => setHoveredCard(item.id)}
                      onMouseLeave={() => setHoveredCard(null)}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr',
                        gap: '1rem',
                        padding: '1rem 1.5rem',
                        borderBottom: '1px solid #f3f4f6',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        backgroundColor: hoveredCard === item.id ? '#f9fafb' : 'white',
                        transform: hoveredCard === item.id ? 'translateY(-1px)' : 'translateY(0)',
                        boxShadow: hoveredCard === item.id ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none'
                      }}
                    >
                      {/* Name */}
                      <div style={{
                        fontWeight: 600,
                        color: '#1f2937',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        {item.name || item.title}
                        {!isProject && item.projectName && (
                          <span style={{
                            fontSize: '0.75rem',
                            color: '#6b7280',
                            fontWeight: 400
                          }}>
                            ({item.projectName})
                          </span>
                        )}
                      </div>

                      {/* Type */}
                      <div>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '1rem',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          backgroundColor: isProject ? '#dbeafe' : '#fed7aa',
                          color: isProject ? '#1e40af' : '#ea580c'
                        }}>
                          {isProject ? 'PROJECT' : 'PROJECT-TASK'}
                        </span>
                      </div>

                      {/* Status */}
                      <div>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '1rem',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          backgroundColor: `${statusColor}20`,
                          color: statusColor
                        }}>
                          {item.status || 'Pending'}
                        </span>
                      </div>

                      {/* Due Date */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.875rem',
                        color: '#6b7280'
                      }}>
                        <Calendar size={14} />
                        {new Date(item.deadline || item.dueDate).toLocaleDateString('en-GB')}
                      </div>

                      {/* Budget */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.875rem',
                        color: '#6b7280'
                      }}>
                        {isProject ? (
                          <>
                            <IndianRupee size={14} />
                            {item.budget?.toLocaleString('en-IN') || 0}
                          </>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>-</span>
                        )}
                      </div>

                      {/* Team */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.875rem',
                        color: '#6b7280'
                      }}>
                        {isProject ? (
                          <>
                            <User size={14} />
                            {item.teamMembers?.length || 0} members
                          </>
                        ) : (
                          <>
                            <User size={14} />
                            <span style={{ color: '#1f2937', fontWeight: 600 }}>
                              {getEmployeeName(item.assignedTeamMember)}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default ProjectTracker;


